import api from '@/api/client';
import { env, servicePath } from '@/config/env';
import { pickMediaUrl } from '@/lib/pickMediaUrl';
import type {
  AuthResponse,
  CreateUserPayload,
  LoginRequest,
  RegisterRequest,
  TypeaheadItem,
  TypeaheadRequest,
  TypeaheadType,
  ValidateEmailResult,
  ValidateUsernameRequest,
  ValidateUsernameResult,
} from '@/features/auth/api.types';
import { ACCOUNT_TYPE_TO_USER_TYPE_ID } from '@/features/auth/constants';
import type { AccountType } from '@/features/auth/types';

const MOCK_DELAY_MS = 400;
const DEVICE_TOKEN_FALLBACK = 'no-token';

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function formatDisplayNameFromEmail(email: string): string {
  const localPart = email.split('@')[0] || 'user';
  return (
    localPart
      .split(/[._-]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ') || 'User'
  );
}

function readOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return undefined;
}

async function mockLogin({ username, password }: LoginRequest): Promise<AuthResponse> {
  await delay(MOCK_DELAY_MS);

  if (!username.trim() || !password.trim()) {
    throw new Error('Username and password are required.');
  }

  return {
    user: {
      id: 'mock-user-1',
      name: formatDisplayNameFromEmail(username),
      email: username.includes('@') ? username.trim() : `${username.trim()}@example.com`,
    },
    token: 'mock-jwt-token',
  };
}

async function mockRegister({
  firstName,
  lastName,
  email,
  password,
}: RegisterRequest): Promise<AuthResponse> {
  await delay(MOCK_DELAY_MS);

  if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
    throw new Error('Please complete all required fields.');
  }

  return {
    user: {
      id: 'mock-user-1',
      name: `${firstName.trim()} ${lastName.trim()}`,
      email: email.trim(),
    },
    token: 'mock-jwt-token',
  };
}

/** Defensive extraction of `{ user, token }` from the MyU login payload. */
export function normalizeLoginResponse(payload: unknown, fallbackUsername: string): AuthResponse {
  const root = (payload ?? {}) as Record<string, unknown>;
  const data = (root.data ?? root.result ?? root) as Record<string, unknown>;
  const nestedUser = (data.user ?? root.user) as Record<string, unknown> | undefined;

  const token =
    (root.token as string | undefined) ??
    (root.loggedInToken as string | undefined) ??
    (root.authToken as string | undefined) ??
    (root.accessToken as string | undefined) ??
    (root.access_token as string | undefined) ??
    (data.token as string | undefined) ??
    (data.loggedInToken as string | undefined) ??
    (data.authToken as string | undefined) ??
    (data.accessToken as string | undefined) ??
    (data.access_token as string | undefined) ??
    (nestedUser?.token as string | undefined) ??
    (nestedUser?.loggedInToken as string | undefined) ??
    (nestedUser?.authToken as string | undefined) ??
    '';

  const userRaw = ((data.user as Record<string, unknown> | undefined) ?? data) as Record<
    string,
    unknown
  >;

  // Some MyU endpoints return `board`, `boards`, etc. as siblings of `user`
  // (i.e. directly on `data`). Merge them into the persisted `rawUser` so
  // downstream consumers (sidebar owned classes, class resolution, …) can
  // find them at a single, predictable location.
  const mergedRawUser: Record<string, unknown> = { ...data, ...userRaw };

  const id = String(
    userRaw.id ?? userRaw.userId ?? userRaw._id ?? userRaw.uuid ?? fallbackUsername,
  );
  const firstName = (userRaw.firstName as string | undefined) ?? '';
  const lastName = (userRaw.lastName as string | undefined) ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const username = (userRaw.username as string | undefined) ?? '';
  const name =
    (userRaw.name as string | undefined) ??
    (userRaw.fullName as string | undefined) ??
    (userRaw.displayName as string | undefined) ??
    (fullName || username || fallbackUsername);
  const email =
    (userRaw.email as string | undefined) ??
    (userRaw.emailAddress as string | undefined) ??
    (fallbackUsername.includes('@') ? fallbackUsername : `${fallbackUsername}@myu.co`);
  const avatarUrl = pickMediaUrl(
    userRaw,
    'profileImageThumbnail',
    'profileImage',
    'avatarUrl',
    'profilePic',
    'photo',
    'profilePicture',
    'imageUrl',
    'image',
  );
  const role = (userRaw.role as string | undefined) ?? (userRaw.userType as string | undefined);
  const schoolId =
    (userRaw.schoolId as string | undefined) ?? (userRaw.school_id as string | undefined);
  const university = userRaw.university as Record<string, unknown> | undefined;
  const schoolName =
    (userRaw.schoolName as string | undefined) ??
    (userRaw.school_name as string | undefined) ??
    (typeof university?.name === 'string' ? university.name : undefined) ??
    (typeof university?.abbreviation === 'string' ? university.abbreviation : undefined) ??
    (userRaw.institution as string | undefined) ??
    (userRaw.organisation as string | undefined);
  const status = readOptionalBoolean(userRaw.status);

  return {
    user: {
      id,
      name,
      email,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      username: username || undefined,
      avatarUrl,
      role,
      schoolId,
      schoolName,
      status,
    },
    token,
    rawUser: mergedRawUser,
  };
}

/**
 * Read the auth token from a login/register response's headers.
 *
 * MyU backends return the session token in the `myu-auth-token` response header.
 * In modern axios (≥1.x) `response.headers` is an `AxiosHeaders` instance with a
 * `.get()` method; in older versions / mocks it can be a plain object. This
 * helper handles both, plus any header-name casing and a few fallbacks.
 *
 * IMPORTANT: For the browser to actually expose this header to JavaScript the
 * backend MUST send `Access-Control-Expose-Headers: myu-auth-token` (otherwise
 * the value will be `undefined` even though it's visible in DevTools network tab).
 */
function extractTokenFromHeaders(headers: unknown): string | undefined {
  if (!headers) return undefined;

  const candidates = ['myu-auth-token', 'x-auth-token', 'authorization'];

  // AxiosHeaders instance (has a `.get()` method).
  const maybeAxiosHeaders = headers as { get?: (name: string) => unknown };
  if (typeof maybeAxiosHeaders.get === 'function') {
    for (const name of candidates) {
      const value = maybeAxiosHeaders.get(name);
      if (typeof value === 'string' && value.trim()) {
        return name === 'authorization' ? value.replace(/^Bearer\s+/i, '').trim() : value.trim();
      }
    }
  }

  // Plain object fallback (case-insensitive lookup).
  const dict = headers as Record<string, unknown>;
  const keys = Object.keys(dict);
  for (const name of candidates) {
    const matchKey = keys.find((k) => k.toLowerCase() === name);
    if (!matchKey) continue;
    const value = dict[matchKey];
    if (typeof value === 'string' && value.trim()) {
      return name === 'authorization' ? value.replace(/^Bearer\s+/i, '').trim() : value.trim();
    }
  }

  return undefined;
}

/** POST /<userservice>/api/<version>/user/login — uses mock when `env.useMockAuth` is true. */
export async function login(request: LoginRequest): Promise<AuthResponse> {
  if (env.useMockAuth) {
    return mockLogin(request);
  }

  const response = await api.post<unknown>(
    servicePath('user', 'user/login'),
    {
      username: request.username,
      password: request.password,
      deviceType: env.deviceType || 'webapp',
    },
    // The login form renders its own ErrorDialog; skip the global toast so
    // the user does not see two stacked "Username or password incorrect"
    // popups for the same failed login.
    { meta: { suppressGlobalErrorToast: true } } as never,
  );

  const headerToken = extractTokenFromHeaders(response.headers);
  const normalized = normalizeLoginResponse(response.data, request.username);
  const token = headerToken || normalized.token;

  // A login without a usable credential must not be treated as successful:
  // doing so would skip updateinfo and send every subsequent app request with
  // no authentication. Keep the user on the login screen instead.
  if (!token?.trim()) {
    throw new Error(
      'Login succeeded but no session token was returned. Please try signing in again.',
    );
  }

  if (!Number.isFinite(Number(normalized.user.id))) {
    throw new Error('Login succeeded but no valid user ID was returned. Please try again.');
  }

  return { ...normalized, token: token.trim() };
}

/**
 * Map a `RegisterRequest` (form state) → exact wire payload expected by
 * `POST /userservice/api/v3/user`:
 *
 *   {
 *     "userTypeId": 2,            // student=2, instructor=1, management=3, parent=5
 *     "universityId": 279817,
 *     "name": "Sass",             // first name
 *     "surName": "ssss",          // last name
 *     "mobileNo": "9718510937",
 *     "username": "ss_qa",
 *     "email": "k@gmail.co",
 *     "password": "123456",
 *     "child": "",
 *     "deviceType": "web"
 *   }
 */
export function buildCreateUserPayload(request: RegisterRequest): CreateUserPayload {
  return {
    userTypeId: ACCOUNT_TYPE_TO_USER_TYPE_ID[request.accountType],
    universityId: request.schoolId,
    name: request.firstName.trim(),
    surName: request.lastName.trim(),
    mobileNo: (request.mobileNo ?? '').trim(),
    username: request.username.trim(),
    email: request.email.trim(),
    password: request.password,
    child: request.child ?? '',
    // The curl shows the literal string "web" here (not "webapp"). Keep parity.
    deviceType: 'web',
  };
}

/** POST /<userservice>/api/<version>/user — uses mock when `env.useMockAuth` is true. */
export async function register(request: RegisterRequest): Promise<AuthResponse> {
  if (env.useMockAuth) {
    return mockRegister(request);
  }

  const payload = buildCreateUserPayload(request);

  if (import.meta.env.DEV) {
    console.info('[auth/register] POST', servicePath('user', 'user'), payload);
  }

  // Public endpoint — explicitly send an empty `myu-auth-token` header so the
  // request interceptor doesn't attach a stale token from a previous session.
  const response = await api.post<unknown>(servicePath('user', 'user'), payload, {
    headers: { 'myu-auth-token': '' },
  });

  const headerToken = extractTokenFromHeaders(response.headers);
  const normalized = normalizeLoginResponse(response.data, request.username || request.email);
  const token = headerToken || normalized.token;

  return { ...normalized, token };
}

/**
 * Flatten the MyU `validate-username` envelope into a predictable result.
 *
 * VERIFIED real response shape (note the trap — see below):
 *   taken  → { statusCode: "2XX", data: { availableUser: true,
 *                                         suggestedUserList: ["abhay_1", …] } }
 *   free   → { statusCode: "2XX", data: { availableUser: false } }
 *
 * ⚠️ `availableUser` has INVERTED semantics: `availableUser: true` means
 * "a user with this username ALREADY EXISTS" (i.e. NOT available to register),
 * and the backend then returns alternatives in `suggestedUserList`. We map this
 * onto our `available` flag (which means "free to register") as
 * `available = !availableUser`.
 *
 * A few synonym keys and a suggestions-based fallback are kept so other backend
 * builds keep working.
 */
export function parseValidateUsernameResponse(payload: unknown): ValidateUsernameResult {
  const root = (payload ?? {}) as Record<string, unknown>;
  const data = (root.data ?? root.result ?? root) as Record<string, unknown>;

  const asBool = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  };

  // Suggestions — the MyU backend uses `suggestedUserList`; keep fallbacks for
  // other builds / the field name used in the request payload.
  const suggestionsRaw =
    data.suggestedUserList ??
    data.suggestedUsername ??
    data.suggestedUsernames ??
    data.suggestions ??
    root.suggestedUserList ??
    root.suggestedUsername ??
    [];
  const suggestions = Array.isArray(suggestionsRaw)
    ? suggestionsRaw.filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
    : [];

  // "Taken / already exists" flags — INVERTED polarity (true = taken).
  const takenFlag =
    asBool(data.availableUser) ??
    asBool(data.userExist) ??
    asBool(data.isUserExist) ??
    asBool(data.exist) ??
    asBool(data.taken) ??
    asBool(data.isTaken) ??
    asBool(root.availableUser);

  // Normal-polarity availability flags (true = free) — only consulted when the
  // backend didn't answer with a "taken/exists" flag above.
  const availableFlag =
    asBool(data.available) ??
    asBool(data.isAvailable) ??
    asBool(data.valid) ??
    asBool(data.isValid) ??
    asBool(root.available) ??
    asBool(root.isAvailable);

  let available: boolean;
  if (takenFlag !== undefined) {
    available = !takenFlag;
  } else if (availableFlag !== undefined) {
    available = availableFlag;
  } else {
    // No explicit flag: a populated suggestion list means the name is taken;
    // otherwise treat the bare success envelope as available.
    available = suggestions.length === 0;
  }

  const message =
    (typeof data.message === 'string' ? data.message : undefined) ??
    (typeof root.message === 'string' ? root.message : undefined) ??
    '';

  return { available, message, suggestions };
}

/**
 * POST /<userservice>/api/<version>/user/validate-username
 *
 * Checks whether a username is available during the registration flow and,
 * when taken, surfaces the backend's suggested alternatives. This is a public
 * endpoint (no auth token) and renders its own inline UI, so the global error
 * toast is suppressed.
 *
 * Resolution contract:
 *   • available  → `{ available: true }`
 *   • taken      → `{ available: false, message, suggestions }`
 *   • network/5xx → throws (the caller treats this as "unknown" and stays
 *     neutral instead of falsely claiming the handle is taken).
 */
export async function validateUsername(
  request: ValidateUsernameRequest,
): Promise<ValidateUsernameResult> {
  const username = request.username.trim();
  if (!username) {
    return { available: false, message: '', suggestions: [] };
  }

  if (env.useMockAuth) {
    await delay(MOCK_DELAY_MS);
    // A tiny reserved set so the UI (taken state + suggestions) is exercisable
    // while the real backend is mocked.
    const reserved = new Set(['admin', 'test', 'john', 'johndoe', 'user']);
    const isTaken = reserved.has(username.toLowerCase());
    return {
      available: !isTaken,
      message: isTaken ? 'This username is already taken.' : '',
      suggestions: isTaken
        ? [`${username}1`, `${username}_myu`, `${username}_${new Date().getFullYear()}`]
        : [],
    };
  }

  const path = servicePath('user', 'user/validate-username');
  const payload = {
    username,
    name: (request.name ?? '').trim(),
    surName: (request.surName ?? '').trim(),
    email: (request.email ?? '').trim(),
    suggestedUsername: request.suggestedUsername ? [...request.suggestedUsername] : [],
  };

  if (import.meta.env.DEV) {
    console.info('[auth/validate-username] POST', path, payload);
  }

  try {
    // Public endpoint — send an empty `myu-auth-token` so the request
    // interceptor doesn't attach a stale token, and suppress the global toast
    // because the field renders its own inline availability hint.
    const response = await api.post<unknown>(path, payload, {
      headers: { 'myu-auth-token': '' },
      meta: { suppressGlobalErrorToast: true },
    } as never);
    const result = parseValidateUsernameResponse(response.data);
    if (import.meta.env.DEV) {
      console.info('[auth/validate-username] ←', response.data, '→', result);
    }
    return result;
  } catch (error) {
    const err = error as Error & { statusCode?: string };
    const code = (err.statusCode ?? '').toUpperCase();
    // A 4XX "logical error" envelope (the response interceptor rejects these)
    // means the username is taken/invalid — fold it back into a normal result.
    if (/^4(XX|\d{2})$/.test(code)) {
      return {
        available: false,
        message: err.message || 'This username is not available.',
        suggestions: [],
      };
    }
    // Network / 5xx: re-throw so React Query surfaces it as an error and the
    // UI stays neutral rather than falsely flagging the handle as taken.
    throw err;
  }
}

/** RFC-5322-lite email shape check — good enough to gate the network call. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * GET /<userservice>/api/<version>/user/checkunique?type=email&value=…
 *
 * Public, unauthenticated check for whether an email is already registered in
 * the app. VERIFIED behaviour:
 *   free  → HTTP 200 `{ statusCode: "2XX" }`
 *   taken → `{ statusCode: "4XX", errorMessage: "Email already registered" }`
 *           (the response interceptor turns the `4XX` envelope into a thrown
 *           Error, which we catch and fold into `{ available: false }`).
 *
 * NOTE: This is the app-facing equivalent of the CMS admin `POST
 * https://cms.myu.co/user/validation` form — that endpoint can't be used from
 * the browser app because it lives on a different origin and requires an
 * authenticated CMS admin session (`sessionid` cookie + CSRF token).
 *
 * Resolution contract mirrors `validateUsername`:
 *   • available   → `{ available: true }`
 *   • taken       → `{ available: false, message }`
 *   • network/5xx → throws (caller stays neutral instead of blocking submit).
 */
export async function validateEmail(email: string): Promise<ValidateEmailResult> {
  const trimmed = email.trim();
  if (!trimmed || !EMAIL_PATTERN.test(trimmed)) {
    return { available: false, message: '' };
  }

  if (env.useMockAuth) {
    await delay(MOCK_DELAY_MS);
    // A tiny reserved set so the taken-state UI is exercisable under the mock.
    const reserved = new Set(['test@example.com', 'admin@myu.co', 'taken@myu.co']);
    const isTaken = reserved.has(trimmed.toLowerCase());
    return {
      available: !isTaken,
      message: isTaken ? 'Email already registered' : '',
    };
  }

  const path = servicePath('user', 'user/checkunique');
  if (import.meta.env.DEV) {
    console.info('[auth/validate-email] GET', path, { type: 'email', value: trimmed });
  }

  try {
    // Public endpoint — send an empty `myu-auth-token` so the request
    // interceptor doesn't attach a stale token, and suppress the global toast
    // because the field renders its own inline hint.
    const response = await api.get<unknown>(path, {
      params: { type: 'email', value: trimmed },
      headers: { 'myu-auth-token': '' },
      meta: { suppressGlobalErrorToast: true },
    } as never);
    if (import.meta.env.DEV) {
      console.info('[auth/validate-email] ← available', response.data);
    }
    // A clean 2XX envelope means the email is free.
    return { available: true, message: '' };
  } catch (error) {
    const err = error as Error & { statusCode?: string };
    const code = (err.statusCode ?? '').toUpperCase();
    // A 4XX "logical error" envelope means the email is already registered.
    if (/^4(XX|\d{2})$/.test(code)) {
      return { available: false, message: err.message || 'Email already registered' };
    }
    // Network / 5xx: re-throw so the UI stays neutral.
    throw err;
  }
}

/** GET /<userservice>/api/<version>/user/session — validate current token (for future app bootstrap). */
export async function getSession(): Promise<AuthResponse | null> {
  if (env.useMockAuth) {
    return null;
  }

  const { data } = await api.get<AuthResponse | null>(servicePath('user', 'user/session'));
  return data;
}

// ---------------------------------------------------------------------------
// Forgot password — sends a reset link to the email on file.
// ---------------------------------------------------------------------------

/**
 * POST /<userservice>/api/<version>/user/forgotPassword?usernameOrEmail=…
 *
 * Public endpoint — no auth token required. The backend returns
 * `{ statusCode: "2XX" }` on success and surfaces validation errors
 * (unknown user, malformed email, …) through the standard error envelope
 * which the response interceptor turns into a thrown `Error`.
 *
 * Wire shape: the verified cURL is a POST with the literal JSON body
 * `null` (i.e. `--data-raw 'null'`) and the email passed as a query
 * string parameter. Sending it as a GET makes the backend respond with
 * `401 Authentication error` because the route is only registered for
 * POST.
 *
 * The form renders its own inline success/error UI, so we suppress the
 * global error toast to avoid stacking two popups for the same failure.
 */
export async function forgotPassword(usernameOrEmail: string): Promise<void> {
  const trimmed = usernameOrEmail.trim();
  if (!trimmed) {
    throw new Error('Please enter your username or email address.');
  }

  if (env.useMockAuth) {
    await delay(MOCK_DELAY_MS);
    return;
  }

  const path = servicePath('user', 'user/forgotPassword');
  if (import.meta.env.DEV) {
    console.info('[auth/forgotPassword] POST', path, { usernameOrEmail: trimmed });
  }

  await api.post(path, null, {
    params: { usernameOrEmail: trimmed },
    // Public endpoint — explicitly send an empty `myu-auth-token` so the
    // request interceptor doesn't attach a stale token from a previous
    // session (matches what the verified cURL sends).
    headers: { 'myu-auth-token': '' },
    // The form has its own inline error banner; skip the global toast.
    meta: { suppressGlobalErrorToast: true },
  } as never);
}

// ---------------------------------------------------------------------------
// Device info — called once after a successful login so the backend knows
// which device/browser the session belongs to.
// ---------------------------------------------------------------------------

export interface UpdateDeviceInfoRequest {
  userDeviceId?: number;
  deviceToken?: string;
  chatDeviceToken?: string;
  deviceType: string;
  appVersion: string;
  userId: number;
  userTypeId?: number;
  osVersion: string;
  deviceName: string;
  loggedInToken: string;
  androidId?: string;
  deviceLanguage?: string;
}

/** Best-effort browser/OS sniffing for the device-info payload. */
export function detectBrowserDeviceInfo(): {
  deviceName: string;
  osVersion: string;
  deviceLanguage: string;
} {
  if (typeof navigator === 'undefined') {
    return { deviceName: 'web', osVersion: 'unknown', deviceLanguage: 'en' };
  }

  const ua = navigator.userAgent || 'web';
  let browser = 'Browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari';

  let osVersion = 'unknown';
  const osMatch =
    ua.match(/Mac OS X ([0-9_.]+)/) ||
    ua.match(/Windows NT ([0-9.]+)/) ||
    ua.match(/Android ([0-9.]+)/) ||
    ua.match(/(?:iPhone|iPad);.*OS ([0-9_]+)/) ||
    ua.match(/Linux ([^;)]+)/);
  if (osMatch) osVersion = osMatch[1].replace(/_/g, '.');

  return {
    deviceName: `${browser} (web)`,
    osVersion,
    deviceLanguage: navigator.language || 'en',
  };
}

/**
 * Best-effort FCM token retrieval for the post-login `updateDeviceInfo` call.
 *
 * Returns the FCM registration token IF the user has previously granted
 * notification permission. Otherwise returns `null` so the caller falls
 * back to `'no-token'`.
 *
 * Internally delegates to `pushService.prefetchToken()`, which:
 *   - is idempotent (multiple callers share a single in-flight promise),
 *   - reuses the token the pre-login `<usePushPermissionGate />` already
 *     warmed in the background, and
 *   - never calls `Notification.requestPermission()` from this path —
 *     `updateDeviceInfo` runs after the login mutation, which is not a
 *     clean user-gesture context. The permission prompt lives on the
 *     login / register screen via `usePushPermissionGate`.
 *
 * Logs the precise failure reason on null so the DevTools console always
 * tells you why `deviceToken` came out as `'no-token'`.
 */
async function tryGetFcmToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  if (typeof Notification === 'undefined') {
    if (import.meta.env.DEV) console.warn('[auth] FCM skipped: Notification API not available');
    return null;
  }
  if (Notification.permission !== 'granted') {
    if (import.meta.env.DEV) {
      console.warn(
        `[auth] FCM skipped: Notification.permission is '${Notification.permission}' (need 'granted')`,
      );
    }
    return null;
  }
  try {
    const { pushService } = await import('@/features/push-notifications');
    const cached = pushService.getCachedToken();
    if (cached) return cached;

    // The login-screen permission gate starts this work in the background,
    // but a fast login response can beat Firebase/service-worker startup.
    // Join the shared prefetch promise here instead of permanently registering
    // this signed-in browser as `no-token` because of that race.
    const prefetched = await pushService.prefetchToken();
    if (prefetched) return prefetched;

    const failure = pushService.getLastPrefetchFailure();
    if (import.meta.env.DEV) {
      console.warn(
        `[auth] FCM token unavailable — prefetch failed with reason: ${failure.reason ?? 'unknown'}`,
        failure.error ?? '',
      );
    }
    return null;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[auth] FCM token retrieval threw (non-fatal):', err);
    }
    return null;
  }
}

export async function buildDeviceInfoRequest(
  userId: string | number,
  loggedInToken: string,
  overrides: Partial<UpdateDeviceInfoRequest> = {},
): Promise<UpdateDeviceInfoRequest | null> {
  const numericUserId = Number(userId);
  if (!Number.isFinite(numericUserId) || !loggedInToken) return null;

  const detected = detectBrowserDeviceInfo();
  const fcmToken = await tryGetFcmToken();

  return {
    deviceToken: fcmToken ?? DEVICE_TOKEN_FALLBACK,
    chatDeviceToken: fcmToken ?? DEVICE_TOKEN_FALLBACK,
    deviceType: env.deviceType || 'webapp',
    appVersion: env.appVersion || '5.0.0',
    userId: numericUserId,
    osVersion: detected.osVersion,
    deviceName: detected.deviceName,
    loggedInToken,
    androidId: 'no-androidId',
    deviceLanguage: detected.deviceLanguage,
    ...overrides,
  };
}

/**
 * PUT /<userservice>/api/<version>/user/updateinfo/{userId}
 *
 * Notifies the backend about the current device after login so it can register
 * the session in `user_device`. Failures are non-fatal — we log and continue.
 */
export async function updateDeviceInfo(
  userId: string | number,
  loggedInToken: string,
  overrides: Partial<UpdateDeviceInfoRequest> = {},
): Promise<void> {
  const numericUserId = Number(userId);
  if (!Number.isFinite(numericUserId) || !loggedInToken) return;

  const body = await buildDeviceInfoRequest(numericUserId, loggedInToken, overrides);
  if (!body) return;

  try {
    const hasRealPushToken = Boolean(
      body.deviceToken && body.deviceToken !== DEVICE_TOKEN_FALLBACK,
    );
    console.info('[push] registering device token with backend', {
      userId: numericUserId,
      deviceType: body.deviceType,
      hasRealPushToken,
      tokenPrefix: hasRealPushToken ? `${body.deviceToken?.slice(0, 12)}…` : '(no-token)',
    });
    const response = await api.put(servicePath('user', `user/updateinfo/${numericUserId}`), body, {
      // The login response just gave us this token — send it explicitly so
      // we don't depend on the auth store / localStorage being populated yet
      // by the time this fire-and-forget call leaves the browser.
      headers: {
        'myu-auth-token': loggedInToken,
      },
      // Device registration is best-effort and is not a session-validation
      // request. Some newly-created accounts can receive a 401 here while the
      // backend is still provisioning their user-device row. Let the local
      // catch below absorb that failure instead of allowing the global axios
      // interceptor to destroy the valid session we just obtained from login.
      meta: {
        suppressLogoutOn401: true,
        suppressGlobalErrorToast: true,
      },
    } as never);
    console.info('[push] backend device registration completed', {
      userId: numericUserId,
      status: response.status,
      hasRealPushToken,
    });
  } catch (error) {
    console.warn('[push] backend device registration failed', error);
  }
}

// ---------------------------------------------------------------------------
// Typeahead — institution lookup used in the registration "Find Your School"
// step. Backed by `GET /<midl>/api/<version>/typeahed`.
//
// NOTE: the endpoint name `typeahed` (sic) is intentional — that is the actual
// path the MyU backend exposes.
// ---------------------------------------------------------------------------

/**
 * Map the selected registration account type to the typeahead `type` the
 * backend expects.
 *
 * NOTE: the MyU `GET /midl/api/v3/typeahed` endpoint only accepts the value
 * `university` today — passing `school` returns `"Invalid type parameter!!"`.
 * All four registration roles (Student, Teacher/Professor, Department/Club,
 * Parent) therefore share the same lookup until the backend exposes a
 * dedicated K-12 endpoint.
 */
export function typeaheadTypeForAccount(_accountType: AccountType): TypeaheadType {
  return 'university';
}

/** Defensive extraction of an array of items from any of the common MyU envelopes. */
function extractTypeaheadItems(payload: unknown): TypeaheadItem[] {
  if (Array.isArray(payload)) return payload as TypeaheadItem[];
  if (!payload || typeof payload !== 'object') return [];

  const root = payload as Record<string, unknown>;
  const dataObj = root.data as Record<string, unknown> | undefined;
  const candidates: unknown[] = [
    root.data,
    root.result,
    root.results,
    root.content,
    root.items,
    dataObj?.content,
    dataObj?.items,
    dataObj?.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as TypeaheadItem[];
  }
  return [];
}

/**
 * GET /<midl>/api/<version>/typeahed?type=…&query=…&page=…&size=…
 *
 * Returns the list of institution suggestions for the registration flow.
 * This endpoint is public — it does NOT require an auth token.
 */
export async function searchInstitutions({
  type,
  query,
  page = 0,
  size = 20,
}: TypeaheadRequest): Promise<TypeaheadItem[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const path = servicePath('midl', 'typeahed');
  if (import.meta.env.DEV) {
    console.info('[auth/typeahead] GET', path, { type, query: trimmed, page, size });
  }

  const response = await api.get<unknown>(path, {
    params: { type, query: trimmed, page, size },
    // The typeahead endpoint is unauthenticated; explicitly send an empty
    // token so the request interceptor doesn't attach a stale one.
    headers: { 'myu-auth-token': '' },
  });

  const items = extractTypeaheadItems(response.data);
  if (import.meta.env.DEV) {
    console.info('[auth/typeahead] ←', items.length, 'item(s)', response.data);
  }
  return items;
}
